import {Component, OnInit, ElementRef, OnDestroy, HostListener} from '@angular/core';
import {TareaService} from "@app/modulo-agenda/tarea/tarea.service";
import {forkJoin, of} from "rxjs";
import {catchError} from "rxjs/operators";
import {ChatTemaService} from "@app/modulo-agenda/tema/chat-tema/chat-tema.service";
import {ChatTareaService} from "@app/modulo-agenda/tarea/chat-tarea/chat-tarea.service";
import {UsuarioService} from "@app/modulo-usuario/usuario/usuario.service";
import {Router} from "@angular/router";
import {
    NotificationCenterGroup,
    NotificationCenterItem,
    NotificationCenterResult,
    NotificationCenterService
} from "./notification-center.service";

const POLLING_INTERVAL_MS = 5 * 60 * 1000;
const UNDO_WINDOW_MS = 8000;

interface NotificationUndoNotice {
    message: string;
    items: NotificationCenterItem[];
}

@Component({
    selector: 'sa-activities',
    templateUrl: './activities.component.html',
    styleUrls: ['./activities.component.css'],
})
export class ActivitiesComponent implements OnInit, OnDestroy {
    count: number;
    lastUpdate: any;
    active: boolean;
    loading: boolean;
    hasError: boolean;
    public notifications: NotificationCenterItem[];
    public visibleNotifications: NotificationCenterItem[];
    public notificationGroups: NotificationCenterGroup[];
    public selectedFilter: string;
    public urgentCount: number;
    public filterCounts: { [id: string]: number };
    public notificationFilters: any[];
    public undoNotice: NotificationUndoNotice;

    private pollingHandle: any;
    private undoNoticeHandle: any;

    constructor(
        private el: ElementRef,
        private tareaService: TareaService,
        private chatTemaService: ChatTemaService,
        private chatTareaService: ChatTareaService,
        private usuarioService: UsuarioService,
        private router: Router,
        private notificationCenterService: NotificationCenterService
    ) {
        this.active = false;
        this.loading = false;
        this.hasError = false;
        this.count = 0;
        this.lastUpdate = new Date();
        this.notifications = [];
        this.visibleNotifications = [];
        this.notificationGroups = [];
        this.selectedFilter = 'all';
        this.urgentCount = 0;
        this.filterCounts = {all: 0, tarea: 0, mensaje: 0, aviso: 0};
        this.undoNotice = null;
        this.undoNoticeHandle = null;
        this.notificationFilters = [
            {id: 'all', label: 'Todo', icon: 'fa-bell-o'},
            {id: 'tarea', label: 'Tareas', icon: 'fa-tasks'},
            {id: 'mensaje', label: 'Mensajes', icon: 'fa-comments-o'},
            {id: 'aviso', label: 'Avisos', icon: 'fa-info-circle'},
        ];
    }

    ngOnInit() {
        this.update();
        this.pollingHandle = setInterval(() => {
            if (!this.active && !this.loading) {
                this.update();
            }
        }, POLLING_INTERVAL_MS);
    }

    ngOnDestroy() {
        if (this.pollingHandle) {
            clearInterval(this.pollingHandle);
            this.pollingHandle = null;
        }
        this.notificationCenterService.clearTimers();
        this.clearUndoNotice();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMouseDown(event: MouseEvent) {
        if (!this.active) {
            return;
        }
        if (!this.el.nativeElement.contains(event.target)) {
            this.active = false;
        }
    }

    onToggle() {
        this.active = !this.active;
    }

    update() {
        this.loading = true;
        this.hasError = false;

        forkJoin([
            this.tareaService.obtenerTareasAsignadas().pipe(catchError(() => { this.hasError = true; return of([]); })),
            this.chatTemaService.getUnreadChats().pipe(catchError(() => { this.hasError = true; return of([]); })),
            this.chatTareaService.getUnreadChats().pipe(catchError(() => { this.hasError = true; return of([]); }))
        ]).subscribe(([tareasAsignadas, chatsTemaNoLeidos, chatsTareaNoLeidos]: [any, any, any]) => {
            this.usuarioService
                .getProximosCumpleanos()
                .then((pc) => {
                    let proximosCumpleanos = pc.filter(x => x.dias_para_cumpleanos <= 7);
                    this.applyCenterResult(this.notificationCenterService.buildNotifications(
                        tareasAsignadas,
                        chatsTemaNoLeidos,
                        chatsTareaNoLeidos,
                        proximosCumpleanos
                    ));
                })
                .catch(() => {
                    this.hasError = true;
                    this.applyCenterResult(this.notificationCenterService.buildNotifications(
                        tareasAsignadas,
                        chatsTemaNoLeidos,
                        chatsTareaNoLeidos,
                        []
                    ));
                });
        });
    }

    public setFilter(filter: string) {
        this.selectedFilter = filter;
        this.rebuildVisibleNotifications();
    }

    public trackByNotificationId(index: number, item: NotificationCenterItem) {
        return item.id;
    }

    public trackByGroupId(index: number, item: NotificationCenterGroup) {
        return item.id;
    }

    public openNotification(item: NotificationCenterItem) {
        if (!item.actionable) {
            return;
        }

        if (item.routeType == 'tarea') {
            this.router.navigate(["/agenda/tareas/1"], {
                queryParams: {
                    'idEstadoTarea': 1,
                    'id': item.routeId,
                }
            });
        } else if (item.routeType == 'tema') {
            this.router.navigate(['/agenda/tema/timeline/' + item.routeId]);
        }
        this.active = false;
    }

    public markNotificationAsRead(item: NotificationCenterItem, event?: any) {
        if (event) {
            event.stopPropagation();
        }
        this.notificationCenterService.markAsRead(item.id);
        this.registerUndoNotice('Notificación marcada como vista', [item]);
        this.removeNotification(item.id);
    }

    public markVisibleAsRead(event?: any) {
        if (event) {
            event.stopPropagation();
        }
        if (!this.visibleNotifications.length) {
            return;
        }
        let items = this.visibleNotifications.slice();
        this.notificationCenterService.markManyAsRead(items);
        this.registerUndoNotice(this.getBulkReadMessage(items.length), items);
        this.notifications = this.notifications.filter(x => items.indexOf(x) == -1);
        this.recalculateSummary();
    }

    public snoozeNotification(item: NotificationCenterItem, option: string, event?: any) {
        if (event) {
            event.stopPropagation();
        }
        this.notificationCenterService.snooze(item.id, option);
        this.registerUndoNotice('Tarea pospuesta ' + this.getSnoozeLabel(option), [item]);
        this.removeNotification(item.id);
    }

    public undoLastNotificationAction(event?: any) {
        if (event) {
            event.stopPropagation();
        }
        if (!this.undoNotice) {
            return;
        }

        let items = this.undoNotice.items.slice();
        this.notificationCenterService.restoreItems(items);
        this.restoreNotifications(items);
        this.clearUndoNotice();
    }

    private applyCenterResult(result: NotificationCenterResult) {
        this.notifications = result.notifications;
        this.urgentCount = result.urgentCount;
        this.filterCounts = {
            all: result.notifications.length,
            tarea: result.taskCount,
            mensaje: result.messageCount,
            aviso: result.noticeCount,
        };
        this.count = this.notifications.length;
        this.lastUpdate = new Date();
        this.loading = false;
        this.rebuildVisibleNotifications();
    }

    private rebuildVisibleNotifications() {
        this.visibleNotifications = this.notificationCenterService.filterNotifications(this.notifications, this.selectedFilter);
        this.notificationGroups = this.notificationCenterService.groupNotifications(this.notifications, this.selectedFilter);
    }

    private removeNotification(id: string) {
        this.notifications = this.notifications.filter(x => x.id != id);
        this.recalculateSummary();
    }

    private restoreNotifications(items: NotificationCenterItem[]) {
        let restored = this.notifications.slice();
        for (let i = 0; i < items.length; i++) {
            let exists = false;
            for (let j = 0; j < restored.length; j++) {
                if (restored[j].id == items[i].id) {
                    exists = true;
                    break;
                }
            }
            if (!exists) {
                restored.push(items[i]);
            }
        }
        this.notifications = this.notificationCenterService.sortNotifications(restored);
        this.recalculateSummary();
    }

    private registerUndoNotice(message: string, items: NotificationCenterItem[]) {
        this.clearUndoNotice();
        this.undoNotice = {
            message: message,
            items: items.slice()
        };
        this.undoNoticeHandle = setTimeout(() => {
            this.undoNotice = null;
            this.undoNoticeHandle = null;
        }, UNDO_WINDOW_MS);
    }

    private clearUndoNotice() {
        if (this.undoNoticeHandle) {
            clearTimeout(this.undoNoticeHandle);
            this.undoNoticeHandle = null;
        }
        this.undoNotice = null;
    }

    private getBulkReadMessage(count: number) {
        if (count == 1) {
            return 'Notificación marcada como vista';
        }
        return count + ' notificaciones marcadas como vistas';
    }

    private getSnoozeLabel(option: string) {
        if (option == '15m') {
            return '15 minutos';
        }
        if (option == '1h') {
            return '1 hora';
        }
        return 'hasta mañana';
    }

    private recalculateSummary() {
        let counts = {all: this.notifications.length, tarea: 0, mensaje: 0, aviso: 0};
        let urgent = 0;
        for (let i = 0; i < this.notifications.length; i++) {
            let kind = this.notifications[i].kind;
            if (counts[kind] !== undefined) {
                counts[kind]++;
            }
            if (this.notifications[i].priority == 'urgent') {
                urgent++;
            }
        }
        this.filterCounts = counts;
        this.urgentCount = urgent;
        this.count = this.notifications.length;
        this.rebuildVisibleNotifications();
    }
}
